import { useEffect, useState } from 'react';

import { Modals } from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  maybeShowKeepGoingPromo,
  playerSessionsSelectors,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useBookmarkSessionMutation,
} from '@/store';
import { Location, Referrer } from '@/types';
import { SnackbarUtils } from '@/utils';

export const useBookmarkManagement = ({
  episodeId,
  referrer,
  location,
}: {
  episodeId: string;
  referrer?: Referrer;
  location?: Location;
}) => {
  const dispatch = useAppDispatch();
  const [bookmarkSession, { isLoading: isBookmarkSessionLoading }] =
    useBookmarkSessionMutation();

  const userId = useAppSelector(selectSessionUserId);

  const [isBookmarkedOptimistic, setIsBookmarkedOptimistic] = useState(false);

  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );

  const isBookmarked = !!playerSession?.extraStates?.bookmark;

  useEffect(() => {
    if (isBookmarked !== undefined) {
      setIsBookmarkedOptimistic(isBookmarked);
    }
  }, [isBookmarked]);

  return {
    toggleBookmark: async () => {
      if (!userId) {
        dispatch(
          uiActions.setActiveModal({
            name: Modals.SIGN_UP,
            params: {
              referrer: {
                ...location,
                button: EVENT_CONSTANTS.BUTTON.BOOKMARK,
              },
            },
          }),
        );
        return;
      }

      setIsBookmarkedOptimistic(prev => !prev);

      const newIsBookmarked = !isBookmarked;

      try {
        await bookmarkSession({
          episodeId,
          newIsBookmarked,
        }).unwrap();
      } catch {
        SnackbarUtils.error('There was a problem updating the bookmark');
        setIsBookmarkedOptimistic(!!isBookmarked);
        return;
      }

      SnackbarUtils.success(
        newIsBookmarked ? 'Episode bookmarked' : 'Episode bookmark removed',
      );

      if (newIsBookmarked) dispatch(maybeShowKeepGoingPromo());

      AnalyticsService.bookmark({
        episodeId,
        value: newIsBookmarked,
        location,
        referrer,
      });
    },
    isBookmarking: isBookmarkSessionLoading,
    isBookmarked: isBookmarkedOptimistic,
  };
};
